import { SlateEditor, SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import { Editor, Node, Range, Transforms } from 'slate'

// Simple URL validation function
const isUrl = (text: string) => {
  try {
    return Boolean(new URL(text))
  } catch (e) {
    return false
  }
}

export function withLinks(editor: SlateEditor, options: any = {}) {
  if (options?.disabled) {
    return editor
  }

  const { isInline, insertData, insertText, onKeyDown } = editor

  editor.isInline = (element: SlateNode) => {
    // Skip links with URLs that match the pattern "mention:*"
    if (
      element.type === EditorElementTypes.LINK &&
      ((element as any).link?.startsWith('mention:') || (element as any).url?.startsWith('mention:'))
    ) {
      return isInline(element)
    }
    return element.type === EditorElementTypes.LINK ? true : isInline(element)
  }

  editor.insertLink = (url: string, label?: string) => {
    if (!url) {
      return
    }

    // Skip links with URLs that match the pattern "mention:*"
    if (url.startsWith('mention:')) {
      return
    }

    const { selection } = editor
    const isCollapsed = selection && Range.isCollapsed(selection)
    const displayText = label || url
    const link = {
      type: EditorElementTypes.LINK,
      url,
      children: isCollapsed ? [{ text: displayText }] : [],
    }

    if (isCollapsed) {
      Transforms.insertNodes(editor, link)
    } else {
      Transforms.wrapNodes(editor, link, { split: true })
      Transforms.collapse(editor, { edge: 'end' })
    }
  }

  editor.insertData = data => {
    const text = data.getData('text/plain')

    if (text && isUrl(text) && !text.startsWith('mention:')) {
      editor.insertLink(text)
    } else {
      insertData(data)
    }
  }

  editor.insertText = text => {
    if (text && isUrl(text) && !text.startsWith('mention:')) {
      editor.insertLink(text)
    } else {
      insertText(text)
    }
  }

  // Add onKeyDown handler to terminate link element when a key is pressed
  editor.onKeyDown = (editor, event) => {
    // Check if we're at the end of a link element
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [linkNode] = Editor.nodes(editor, {
        // @ts-ignore
        match: n => !Editor.isEditor(n) && n.type === EditorElementTypes.LINK,
        at: selection,
      }) || [null]

      // If we're in a link and a key is pressed, handle it specially
      // Skip links with URLs that match the pattern "mention:*"
      if (linkNode && !(linkNode[0] as any).url?.startsWith('mention:')) {
        // Check if we're at the end of the link text
        const linkText = Node.string(linkNode[0])
        const linkPath = linkNode[1]
        const point = selection.anchor
        const [parentNode] = Editor.parent(editor, point.path)
        const offset = point.offset

        // If we're at the end of the link text and a printable character or space is pressed
        if (
          offset === linkText.length &&
          (event.key.length === 1 || event.key === ' ' || event.key === 'Enter' || event.key === 'Tab')
        ) {
          event.preventDefault()

          // Move the selection out of the link
          Transforms.select(editor, Editor.after(editor, linkPath))

          // Insert the character that was pressed
          if (event.key.length === 1) {
            Transforms.insertText(editor, event.key)
          } else if (event.key === ' ') {
            Transforms.insertText(editor, ' ')
          } else if (event.key === 'Enter') {
            Transforms.splitNodes(editor)
          } else if (event.key === 'Tab') {
            // Let the default tab behavior happen
            return
          }

          return // Don't call the original onKeyDown handler
        }
      }
    }

    // Call the original onKeyDown handler
    if (onKeyDown) {
      onKeyDown(editor, event)
    }
  }

  return editor
}
